import { useState, useEffect, useMemo } from "react";
import { ReviewDecisionStatus } from "../../../../../utils/enums";

export const useUserReviewState = (users, currentAssessment) => {
  const [decisions, setDecisions] = useState({});
  const [commentsByUser, setCommentsByUser] = useState({});
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [bulkDecision, setBulkDecision] = useState("");
  const [bulkComment, setBulkComment] = useState("");

  useEffect(() => {
    if (users.length > 0 && currentAssessment) {
      const defaultDecisions = {};
      const defaultComments = {};
      users.forEach((user) => {
        const status = user.review_status || ReviewDecisionStatus.APPROVED;
        defaultDecisions[user.id] = status;
        defaultComments[user.id] = user.review_notes || "";
      });
      setDecisions(defaultDecisions);
      setCommentsByUser(defaultComments);
    }
  }, [users, currentAssessment]);

  useEffect(() => {
    if (currentAssessment?.id) {
      setSelectedUsers([]);
      setBulkDecision("");
      setBulkComment("");
    }
  }, [currentAssessment?.id]);

  const allUsersReviewed = useMemo(() => {
    // Check bulk mode validation first
    if (selectedUsers.length >= 2 && bulkDecision) {
      if (bulkDecision === ReviewDecisionStatus.MODIFIED || bulkDecision === ReviewDecisionStatus.REJECTED) {
        if (!bulkComment || !bulkComment.trim()) {
          return false;
        }
      }
    }
    
    return users.every((user) => {
      if (selectedUsers.includes(user.id) && selectedUsers.length >= 2 && bulkDecision) {
        return true;
      }
      
      const decision = decisions[user.id];
      
      const hasValidDecision = decision === ReviewDecisionStatus.APPROVED || 
                              decision === ReviewDecisionStatus.REJECTED || 
                              decision === ReviewDecisionStatus.MODIFIED;
      
      if (!hasValidDecision) {
        return false;
      }
      
      if (decision === ReviewDecisionStatus.MODIFIED || decision === ReviewDecisionStatus.REJECTED) {
        const comment = commentsByUser[user.id] || "";
        if (!comment.trim()) {
          return false;
        }
      }
      
      return true;
    });
  }, [users, decisions, commentsByUser, selectedUsers, bulkDecision, bulkComment]);

  const submitValidationMessage = useMemo(() => {
    if (selectedUsers.length >= 2 && bulkDecision) {
      if (bulkDecision === ReviewDecisionStatus.MODIFIED || bulkDecision === ReviewDecisionStatus.REJECTED) {
        if (!bulkComment || !bulkComment.trim()) {
          return `Please provide a comment for the bulk ${bulkDecision === ReviewDecisionStatus.MODIFIED ? 'Modify' : 'Revoke'} action`;
        }
      }
    }
    
    const usersWithoutComments = users.filter((user) => {
      if (selectedUsers.includes(user.id) && selectedUsers.length >= 2) {
        return false;
      }
      
      const decision = decisions[user.id];
      if (decision === ReviewDecisionStatus.MODIFIED || decision === ReviewDecisionStatus.REJECTED) {
        const comment = commentsByUser[user.id] || "";
        return !comment.trim();
      }
      return false;
    });
    
    if (usersWithoutComments.length > 0) {
      return `Please provide comments for ${usersWithoutComments.length} user(s) with Modified/Revoked decision`;
    }
    return "";
  }, [users, decisions, commentsByUser, selectedUsers, bulkDecision, bulkComment]);

  const handleDecisionChange = (userId, decision) => {
    setDecisions((prev) => ({ ...prev, [userId]: decision }));
    if (decision === ReviewDecisionStatus.APPROVED) {
      setCommentsByUser((prev) => ({ ...prev, [userId]: "" }));
    }
  };

  const handleCommentChange = (userId, comment) => {
    setCommentsByUser((prev) => ({
      ...prev,
      [userId]: comment,
    }));
  };

  const handleUserSelection = (userId) => {
    setSelectedUsers((prev) => {
      const isCurrentlySelected = prev.includes(userId);
      
      if (isCurrentlySelected) {
        return prev.filter((id) => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
      setBulkDecision("");
      setBulkComment("");
    } else {
      setSelectedUsers(users.map((u) => u.id));
      setBulkDecision("");
      setBulkComment("");
    }
  };

  const handleBulkDecisionChange = (decision) => {
    setBulkDecision(decision);
    const newDecisions = { ...decisions };
    selectedUsers.forEach((userId) => {
      newDecisions[userId] = decision;
    });
    setDecisions(newDecisions);

    if (decision === ReviewDecisionStatus.APPROVED) {
      const newComments = { ...commentsByUser };
      selectedUsers.forEach((userId) => {
        newComments[userId] = "";
      });
      setCommentsByUser(newComments);
      setBulkComment("");
    }
  };

  const handleBulkCommentChange = (comment) => {
    setBulkComment(comment);
    const newComments = { ...commentsByUser };
    selectedUsers.forEach((userId) => {
      newComments[userId] = comment;
    });
    setCommentsByUser(newComments);
  };

  const handleBulkSave = () => {
    setSelectedUsers([]);
    setBulkDecision("");
    setBulkComment("");
  };

  return {
    decisions,
    setDecisions,
    commentsByUser,
    setCommentsByUser,
    selectedUsers,
    setSelectedUsers,
    bulkDecision,
    setBulkDecision,
    bulkComment,
    setBulkComment,
    allUsersReviewed,
    submitValidationMessage,
    handleDecisionChange,
    handleCommentChange,
    handleUserSelection,
    handleSelectAll,
    handleBulkDecisionChange,
    handleBulkCommentChange,
    handleBulkSave,
  };
};

